import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Volume2, Loader2, Square, Check } from "lucide-react";
import { toast } from "sonner";

export default function VoiceSettings({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [meta, setMeta] = useState(null);
  const [voice, setVoice] = useState(user?.tts_voice || "nova");
  const [speed, setSpeed] = useState(user?.tts_speed || 1);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/tts/meta/voices").then((r) => { setMeta(r.data); setVoice(r.data.current_voice); setSpeed(r.data.current_speed); });
    return () => stopPreview();
  }, [open]);

  const stopPreview = () => { audioRef.current?.pause(); audioRef.current = null; setPreviewing(false); };

  const preview = async () => {
    if (previewing) return stopPreview();
    setPreviewing(true);
    try {
      const r = await api.get("/tts/preview", { params: { voice, speed }, responseType: "blob" });
      const a = new Audio(URL.createObjectURL(r.data));
      a.onended = () => setPreviewing(false);
      audioRef.current = a;
      await a.play();
    } catch (e) {
      setPreviewing(false);
      toast.error(e?.response?.status === 402 ? "AI credits exhausted — top up your Universal Key balance" : "Could not generate the preview");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/preferences", { tts_voice: voice, tts_speed: speed });
      setUser(data);
      toast.success("Reading voice saved");
      onOpenChange(false);
    } catch { toast.error("Could not save voice settings"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) stopPreview(); onOpenChange(v); }}>
      <DialogContent data-testid="voice-settings-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0A2A5E]">Reading voice</DialogTitle>
          <DialogDescription>Used by every Listen button — chat answers, guides and Academy modules. Audio is generated only when you click Listen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Voice</Label>
            <div className="grid grid-cols-2 gap-2 mt-2" role="radiogroup" aria-label="Reading voice">
              {(meta?.voices || []).map((v) => (
                <button key={v.id} type="button" role="radio" aria-checked={voice === v.id} data-testid={`voice-option-${v.id}`} onClick={() => { stopPreview(); setVoice(v.id); }}
                  className={`text-left rounded-xl border px-3 py-2 text-sm transition-colors ${voice === v.id ? "border-[#F5A623] bg-[#FDECD0] text-[#0A2A5E]" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                  <span className="flex items-center justify-between font-semibold">{v.label.split(" — ")[0]}{voice === v.id && <Check className="w-3.5 h-3.5 text-[#C7761A]" />}</span>
                  <span className="block text-[11px] text-slate-500">{v.label.split(" — ")[1]}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Speed</Label>
              <span data-testid="voice-speed-value" className="text-sm font-semibold text-[#0A2A5E]">{Number(speed).toFixed(2).replace(/0$/, "")}×</span>
            </div>
            <Slider data-testid="voice-speed-slider" className="mt-3" min={meta?.speed_min ?? 0.75} max={meta?.speed_max ?? 1.5} step={0.05} value={[Number(speed)]} onValueChange={([v]) => { stopPreview(); setSpeed(Math.round(v * 100) / 100); }} />
            <div className="flex justify-between text-[11px] text-slate-500 mt-1"><span>Slower 0.75×</span><span>Normal 1×</span><span>Faster 1.5×</span></div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button type="button" variant="outline" data-testid="voice-preview-btn" onClick={preview} className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]">
              {previewing ? <Square className="w-3.5 h-3.5 mr-1.5" /> : <Volume2 className="w-3.5 h-3.5 mr-1.5" />}{previewing ? "Stop preview" : "Preview voice"}
            </Button>
            <Button type="button" data-testid="voice-save-btn" onClick={save} disabled={saving} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
